import React from 'react';
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  InformationCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import type { Insight } from '@/services/analytics.service';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  InformationCircleIcon,
  LightBulbIcon,
};

const SEVERITY_STYLES: Record<string, { border: string; bg: string; icon: string; badge: string }> = {
  critical: {
    border: 'border-l-danger-500',
    bg: 'bg-danger-50 dark:bg-danger-900/10',
    icon: 'text-danger-500',
    badge: 'bg-danger-100 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400',
  },
  warning: {
    border: 'border-l-warning-500',
    bg: 'bg-warning-50 dark:bg-warning-900/10',
    icon: 'text-warning-500',
    badge: 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400',
  },
  info: {
    border: 'border-l-primary-500',
    bg: 'bg-primary-50 dark:bg-primary-900/10',
    icon: 'text-primary-500',
    badge: 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
  },
};

const SOURCE_LABELS: Record<string, string> = {
  incident: 'Incidents',
  threat: 'Threats',
  investigation: 'Investigations',
  'executive-protection': 'Exec Protection',
};

interface InsightsPanelProps {
  insights: Insight[];
}

const InsightsPanel: React.FC<InsightsPanelProps> = ({ insights }) => {
  if (!insights || insights.length === 0) {return null;}

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <LightBulbIcon className="w-5 h-5 text-warning-500" />
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
          Automated Insights
        </h3>
        <span className="text-xs text-secondary-500 dark:text-secondary-400">
          ({insights.length} detected)
        </span>
      </div>
      <div className="space-y-2">
        {insights.map((insight, index) => {
          const styles = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.info;
          const IconComponent = ICON_MAP[insight.icon] || InformationCircleIcon;

          return (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.2 }}
              className={`flex items-start space-x-3 p-3 rounded-lg border-l-4 ${styles.border} ${styles.bg}`}
            >
              <IconComponent className={`w-5 h-5 mt-0.5 flex-shrink-0 ${styles.icon}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 flex-wrap">
                  <span className="text-sm font-medium text-secondary-900 dark:text-white">
                    {insight.title}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles.badge}`}>
                    {insight.severity}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400">
                    {SOURCE_LABELS[insight.source] || insight.source}
                  </span>
                </div>
                <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
                  {insight.message}
                </p>
              </div>
              {insight.deviationPercent !== 0 && (
                <span className={`text-sm font-medium flex-shrink-0 ${
                  insight.deviationPercent > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-success-600 dark:text-success-400'
                }`}>
                  {insight.deviationPercent > 0 ? '+' : ''}{Math.round(insight.deviationPercent * 10) / 10}%
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default InsightsPanel;
